import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { listGames } from "../Games/gameApi";
import { presignUpload, uploadFileToS3, listSplitLayouts } from "./venueGroundsApi";

import FormModal from "../../../components/form/FormModal";
import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./venue-ground.css";

const citySlugify = (city) =>
  String(city || "")
    .trim()
    .toLowerCase()
    .replace(/[@&%*^~`'"!?,.<>()[\]{}|\\/+]+/g, " ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "unknown-city";

const slugifyGroundName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const FALLBACK_TYPES = ["turf", "indoor", "futsal", "tennis_court", "cricket_net"];
const UNITS = ["hour", "slot"];

const emptyForm = {
  name: "",
  type: "turf",
  capacity: "",
  mainImageUrl: "",
  mainImageKey: "",
  pricing: {
    basePrice: "",
    currency: "INR",
    unit: "hour",
  },
};

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];

const getFirstErrorMessage = (errorsObj) => {
  const firstKey = Object.keys(errorsObj || {})[0];
  return firstKey ? errorsObj[firstKey] : "";
};

const getApiErrorMessage = (error, fallback) => {
  const apiMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error;

  if (apiMessage) return apiMessage;

  if (error?.message === "Network Error") {
    return "We couldn’t upload the image. Please check your connection and try again.";
  }

  return error?.message || fallback;
};

const VenueGroundForm = ({
  open,
  mode = "create",
  initialData = null,
  onCancel,
  onSubmit,
  venueCurrency = "INR",
  existingGrounds = [],
  venue,
}) => {
  const [form, setForm] = useState({
    ...emptyForm,
    pricing: {
      ...emptyForm.pricing,
      currency: venueCurrency,
    },
  });

  const [errors, setErrors] = useState({});

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [gameTypes, setGameTypes] = useState(FALLBACK_TYPES);
  const [gameLabels, setGameLabels] = useState({});
  const [loadingTypes, setLoadingTypes] = useState(true);

  const [sharedSpaceType, setSharedSpaceType] = useState("NONE");

  const [sameGroundIds, setSameGroundIds] = useState([]);
  const [samePick, setSamePick] = useState("");

  const [splitLayoutMode, setSplitLayoutMode] = useState("existing");
  const [splitLayoutId, setSplitLayoutId] = useState("");
  const [layouts, setLayouts] = useState([]);
  const [loadingLayouts, setLoadingLayouts] = useState(false);

  const [splitLayoutName, setSplitLayoutName] = useState("");
  const [splitMaxParts, setSplitMaxParts] = useState("");
  const [splitSizeUsed, setSplitSizeUsed] = useState("");
  const [splitRole, setSplitRole] = useState("FULL");
  const [splitUsageMode, setSplitUsageMode] = useState("preset");

  const ASSET_BASE = (
    process.env.REACT_APP_CDN_URL ||
    process.env.REACT_APP_ASSET_BASE_URL ||
    ""
  ).replace(/\/+$/, "");

  const previewSrc = useMemo(() => {
    if (form.mainImageKey) return `${ASSET_BASE}/${form.mainImageKey}`;
    return String(form.mainImageUrl || "").trim();
  }, [ASSET_BASE, form.mainImageKey, form.mainImageUrl]);

  useEffect(() => {
    setErrors({});
    setSelectedFile(null);
    setProgress(0);

    if (!initialData) {
      setForm({
        ...emptyForm,
        pricing: {
          ...emptyForm.pricing,
          currency: venueCurrency,
        },
      });

      setSharedSpaceType("NONE");
      setSameGroundIds([]);
      setSamePick("");

      setSplitLayoutMode("existing");
      setSplitLayoutId("");
      setSplitLayoutName("");
      setSplitMaxParts("");
      setSplitSizeUsed("");
      setSplitRole("FULL");
      setSplitUsageMode("preset");

      return;
    }

    setForm({
      ...emptyForm,
      ...initialData,
      mainImageUrl: initialData.mainImageUrl || "",
      mainImageKey: initialData.mainImageKey || "",
      pricing: {
        basePrice: initialData?.pricing?.basePrice ?? "",
        currency: venueCurrency,
        unit: initialData?.pricing?.unit || "hour",
      },
    });

    const currentSharedType = String(initialData?.sharedSpaceType || "NONE").toUpperCase();
    const safeSharedType = ["NONE", "SAME", "SPLIT"].includes(currentSharedType)
      ? currentSharedType
      : "NONE";

    setSharedSpaceType(safeSharedType);

    if (safeSharedType === "SAME" && initialData?.sharedSpaceKey) {
      const selfId = String(initialData?._id || initialData?.id || "");

      const groupIds = (existingGrounds || [])
        .filter((ground) => ground.sharedSpaceKey === initialData.sharedSpaceKey)
        .map((ground) => String(ground._id || ground.id))
        .filter((id) => id && id !== selfId);

      setSameGroundIds(groupIds);
    } else {
      setSameGroundIds([]);
    }

    if (safeSharedType === "SPLIT") {
      setSplitLayoutMode("existing");
      setSplitLayoutId(initialData?.splitLayoutId || "");

      const role = String(initialData?.sharedSpaceRole || "").toUpperCase();
      const used = String(initialData?.splitSizeUsed ?? "");

      if (["FULL", "HALF_A", "HALF_B"].includes(role)) {
        setSplitUsageMode("preset");
        setSplitRole(role);
        setSplitSizeUsed(role === "FULL" ? "2" : "1");
      } else {
        setSplitUsageMode("custom");
        setSplitRole("CUSTOM");
        setSplitSizeUsed(used || "");
      }
    } else {
      setSplitLayoutMode("existing");
      setSplitLayoutId("");
      setSplitLayoutName("");
      setSplitMaxParts("");
      setSplitSizeUsed("");
      setSplitRole("FULL");
      setSplitUsageMode("preset");
    }
  }, [initialData, venueCurrency, existingGrounds]);

  useEffect(() => {
    let alive = true;

    const loadLayouts = async () => {
      try {
        if (sharedSpaceType !== "SPLIT") return;

        const venueId = venue?._id || venue?.id;
        if (!venueId) return;

        setLoadingLayouts(true);

        const list = await listSplitLayouts(venueId);
        if (!alive) return;

        const nextLayouts = Array.isArray(list) ? list : [];
        setLayouts(nextLayouts);

        if (!splitLayoutId && nextLayouts.length > 0) {
          setSplitLayoutId(nextLayouts[0]._id);
        }
      } catch (error) {
        if (!alive) return;
        setLayouts([]);
        toast.error(getApiErrorMessage(error, "Failed to load split layouts."));
      } finally {
        if (alive) setLoadingLayouts(false);
      }
    };

    loadLayouts();

    return () => {
      alive = false;
    };
  }, [sharedSpaceType, venue?._id, venue?.id, splitLayoutId]);

  useEffect(() => {
    let alive = true;

    const slugifyGame = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");

    const loadTypes = async () => {
      try {
        setLoadingTypes(true);

        const games = await listGames();
        if (!alive) return;

        const fromApi = (Array.isArray(games) ? games : [])
          .map((game) =>
            game?.name
              ? {
                  value: slugifyGame(game.name),
                  label: game.name.trim(),
                }
              : null
          )
          .filter(Boolean);

        const values = fromApi.length > 0 ? fromApi.map((game) => game.value) : FALLBACK_TYPES;

        const labels =
          fromApi.length > 0
            ? fromApi.reduce((acc, game) => {
                acc[game.value] = game.label;
                return acc;
              }, {})
            : FALLBACK_TYPES.reduce((acc, value) => {
                acc[value] = value.replace(/_/g, " ");
                return acc;
              }, {});

        setGameTypes(values);
        setGameLabels(labels);

        setForm((prev) => {
          if (!prev.type || !values.includes(prev.type)) {
            return {
              ...prev,
              type: values[0] || "turf",
            };
          }

          return prev;
        });
      } catch (error) {
        if (!alive) return;

        setGameTypes(FALLBACK_TYPES);
        setGameLabels(
          FALLBACK_TYPES.reduce((acc, value) => {
            acc[value] = value.replace(/_/g, " ");
            return acc;
          }, {})
        );

        setForm((prev) => {
          if (!prev.type || !FALLBACK_TYPES.includes(prev.type)) {
            return {
              ...prev,
              type: FALLBACK_TYPES[0],
            };
          }

          return prev;
        });

        toast.error(getApiErrorMessage(error, "Failed to load game types. Using default types."));
      } finally {
        if (alive) setLoadingTypes(false);
      }
    };

    loadTypes();

    return () => {
      alive = false;
    };
  }, []);

  const set = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setPricing = (key, value) => {
    setForm((prev) => ({
      ...prev,
      pricing: {
        ...(prev.pricing || {}),
        [key]: value,
      },
    }));
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleFilePick = (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      const message = "Unsupported file type. Please upload PNG, JPEG, or WEBP image.";
      clearError("mainImageUrl");
      toast.error(message);
      return;
    }

    if (file.size > MAX_SIZE) {
      const message = "File is too large. Maximum allowed size is 5MB.";
      clearError("mainImageUrl");
      toast.error(message);
      return;
    }

    setSelectedFile(file);
    clearError("mainImageUrl");
    toast.info("Image selected. Click Upload to save it.");
  };

  const doUpload = async () => {
    if (!selectedFile || uploading) return;

    const venueSlug = venue?.slug || "";
    const venueCity = venue?.location?.city || "";
    const groundName = form.name || "";

    if (!venueSlug || !venueCity) {
      const message = "Venue city/slug missing. Please refresh and try again.";
      clearError("mainImageUrl");
      toast.error(message);
      return;
    }

    if (!groundName.trim()) {
      const message = "Please enter ground name before uploading image.";
      clearError("name");
      toast.error(message);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const ext =
        selectedFile.type === "image/png"
          ? "png"
          : selectedFile.type === "image/webp"
          ? "webp"
          : "jpg";

      const folder = `venues/${citySlugify(
        venueCity
      )}/${venueSlug}/grounds/${slugifyGroundName(groundName)}/images`;

      const filename = `main_${Date.now()}.${ext}`;

      console.log("[GROUND UPLOAD] venueSlug:", venueSlug);
      console.log("[GROUND UPLOAD] venueCity:", venueCity);
      console.log("[GROUND UPLOAD] groundName:", groundName);
      console.log("[GROUND UPLOAD] folder:", folder);
      console.log("[GROUND UPLOAD] filename:", filename);
      console.log("[GROUND UPLOAD] contentType:", selectedFile.type);

      const presign = await presignUpload({
        folder,
        filename,
        contentType: selectedFile.type,
      });

      await uploadFileToS3({
        uploadUrl: presign.uploadUrl,
        file: selectedFile,
        contentType: selectedFile.type,
        onProgress: (nextProgress) => setProgress(nextProgress),
      });

      setForm((prev) => ({
        ...prev,
        mainImageKey: presign.filePath,
        mainImageUrl: "",
      }));

      setSelectedFile(null);
      setProgress(100);
      clearError("mainImageUrl");
      toast.success("Ground image uploaded successfully.");
    } catch (error) {
      console.error(error);

      const message = getApiErrorMessage(error, "Upload failed. Please try again.");
      clearError("mainImageUrl");
      toast.error(message);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const handleSharedSpaceTypeChange = (nextType) => {
    setSharedSpaceType(nextType);
    clearError("sameGroundIds");
    clearError("splitLayoutId");
    clearError("splitLayoutName");
    clearError("splitMaxParts");
    clearError("splitRole");
    clearError("splitSizeUsed");

    if (nextType !== "SAME") {
      setSameGroundIds([]);
      setSamePick("");
    }

    if (nextType === "SPLIT") {
      if (splitUsageMode === "custom") {
        setSplitRole("CUSTOM");
        setSplitSizeUsed((prev) => prev || "");
      } else {
        const role = String(splitRole || "FULL").toUpperCase();
        const safeRole = ["FULL", "HALF_A", "HALF_B"].includes(role) ? role : "FULL";

        setSplitRole(safeRole);
        setSplitSizeUsed(safeRole === "FULL" ? "2" : "1");
      }
    } else {
      setSplitLayoutMode("existing");
      setSplitLayoutId("");
      setSplitLayoutName("");
      setSplitMaxParts("");
      setSplitSizeUsed("");
      setSplitRole("FULL");
      setSplitUsageMode("preset");
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(form.name || "").trim()) {
      nextErrors.name = "Name is required";
    }

    if (!form.type) {
      nextErrors.type = "Type is required";
    }

    const capacity = Number(form.capacity);
    if (form.capacity !== "" && (Number.isNaN(capacity) || capacity < 0)) {
      nextErrors.capacity = "Enter a valid capacity";
    }

    const price = Number(form.pricing?.basePrice);
    if (form.pricing?.basePrice === "" || Number.isNaN(price) || price < 0) {
      nextErrors.basePrice = "Base price is required";
    }

    if (sharedSpaceType === "SAME") {
      if (!Array.isArray(sameGroundIds) || sameGroundIds.length < 1) {
        nextErrors.sameGroundIds = "Select at least 1 other ground for same physical space";
      }
    }

    if (sharedSpaceType === "SPLIT") {
      if (!splitRole) {
        nextErrors.splitRole = "Please select space usage";
      }

      const used = Number(splitSizeUsed);
      if (!Number.isFinite(used) || used <= 0) {
        nextErrors.splitSizeUsed = "Please enter valid space usage";
      }

      if (splitLayoutMode === "new") {
        const parts = Number(splitMaxParts);

        if (
          Number.isFinite(parts) &&
          parts > 0 &&
          Number.isFinite(used) &&
          used > parts
        ) {
          nextErrors.splitSizeUsed = `Space usage cannot exceed max parts (${parts})`;
        }
      } else {
        const selectedLayout = (layouts || []).find(
          (layout) => String(layout._id) === String(splitLayoutId)
        );

        const maxParts = Number(selectedLayout?.maxParts || 0);

        if (
          splitLayoutId &&
          Number.isFinite(maxParts) &&
          maxParts > 0 &&
          Number.isFinite(used) &&
          used > maxParts
        ) {
          nextErrors.splitSizeUsed = `Space usage cannot exceed layout max parts (${maxParts})`;
        }
      }

      if (splitLayoutMode === "existing") {
        if (!splitLayoutId) {
          nextErrors.splitLayoutId = "Please select a layout";
        }
      } else {
        if (!String(splitLayoutName || "").trim()) {
          nextErrors.splitLayoutName = "Layout name is required";
        }

        const parts = Number(splitMaxParts);
        if (!Number.isFinite(parts) || parts < 1) {
          nextErrors.splitMaxParts = "Max parts must be >= 1";
        }
      }
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (uploading || submitting) return;

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      toast.error(
        getFirstErrorMessage(validationErrors) || "Please fix validation errors."
      );
      return;
    }

    if (selectedFile) {
      toast.warning("You selected an image but did not upload it. Click Upload first.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      type: form.type,
      capacity: form.capacity === "" ? undefined : Number(form.capacity),
      mainImageKey: form.mainImageKey || undefined,
      mainImageUrl: undefined,

      pricing: {
        basePrice: Number(form.pricing.basePrice),
        currency: venueCurrency,
        unit: form.pricing.unit,
      },

      sharedSpaceType,
      sharedGroundIds: sharedSpaceType === "SAME" ? sameGroundIds : [],
      splitLayoutMode: sharedSpaceType === "SPLIT" ? splitLayoutMode : "existing",
      splitLayoutId: sharedSpaceType === "SPLIT" ? splitLayoutId : "",
      splitLayoutName: sharedSpaceType === "SPLIT" ? splitLayoutName : "",
      splitMaxParts: sharedSpaceType === "SPLIT" ? Number(splitMaxParts || 0) : 0,
      splitSizeUsed: sharedSpaceType === "SPLIT" ? Number(splitSizeUsed || 0) : 0,
      sharedSpaceRole:
        sharedSpaceType === "SPLIT"
          ? splitUsageMode === "custom"
            ? "CUSTOM"
            : splitRole
          : "ALL",
    };

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to save ground. Please try again."));
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (uploading || submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Add Ground" : "Edit Ground"}
      width="760px"
      submitting={uploading || submitting}
      submitText={mode === "create" ? "Add Ground" : "Save Changes"}
      submittingText={uploading ? `Uploading ${progress || 0}%` : "Saving..."}
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <div className="ground-form">
        <div className="ground-grid">
          <FormField label="Ground Name *" error={errors.name} className="ground-span-2">
            <FormInput
              type="text"
              value={form.name}
              onChange={(event) => {
                set("name", event.target.value);
                clearError("name");
              }}
              placeholder="Turf A / Court 1"
              required
            />
          </FormField>

          <FormField label="Type *" error={errors.type}>
            <FormSelect
              value={form.type || ""}
              onChange={(event) => {
                set("type", event.target.value);
                clearError("type");
              }}
              required
              disabled={loadingTypes}
              title={loadingTypes ? "Loading types…" : "Select ground type"}
            >
              {loadingTypes && <option value="">Loading…</option>}

              {!loadingTypes &&
                gameTypes.map((value) => (
                  <option key={value} value={value}>
                    {gameLabels[value] || value.replace(/_/g, " ")}
                  </option>
                ))}
            </FormSelect>
          </FormField>

          <FormField label="Capacity" error={errors.capacity}>
            <FormInput
              type="number"
              min="0"
              value={form.capacity}
              onChange={(event) => {
                set("capacity", event.target.value);
                clearError("capacity");
              }}
              placeholder="e.g., 5 (for 5-a-side)"
            />
          </FormField>

          <div className="ground-span-2">
            <div className="ground-card">
              <FormField label="Shared Space Type">
                <div className="ground-segment">
                  {[
                    { key: "NONE", label: "None" },
                    { key: "SAME", label: "Same physical space" },
                    { key: "SPLIT", label: "Shared capacity / split ground" },
                  ].map((option) => (
                    <label
                      key={option.key}
                      className="ground-pill"
                      data-active={sharedSpaceType === option.key}
                    >
                      <input
                        type="radio"
                        name="sharedSpaceType"
                        value={option.key}
                        checked={sharedSpaceType === option.key}
                        onChange={() => handleSharedSpaceTypeChange(option.key)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </FormField>

              {sharedSpaceType === "SAME" && (
                <div className="ground-shared-block">
                  <FormField
                    label="Select grounds in the same physical space"
                    error={errors.sameGroundIds}
                    hint="Pick at least 1 other ground. All grounds in this group will mutually block each other."
                  >
                    <div className="ground-same-row">
                      <FormSelect
                        value={samePick}
                        onChange={(event) => {
                          const id = event.target.value;
                          setSamePick(id);

                          if (!id) return;

                          setSameGroundIds((prev) => {
                            const next = new Set(prev.map(String));
                            next.add(String(id));
                            return Array.from(next);
                          });

                          setTimeout(() => setSamePick(""), 0);
                          clearError("sameGroundIds");
                        }}
                      >
                        <option value="">+ Add a ground…</option>

                        {(existingGrounds || [])
                          .filter((ground) => {
                            const groundId = String(ground._id || ground.id);
                            const selfId = String(initialData?._id || initialData?.id || "");

                            if (selfId && groundId === selfId) return false;
                            return !sameGroundIds.includes(groundId);
                          })
                          .map((ground) => (
                            <option key={ground._id || ground.id} value={ground._id || ground.id}>
                              {ground.name}
                            </option>
                          ))}
                      </FormSelect>

                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setSameGroundIds([]);
                          setSamePick("");
                        }}
                        disabled={(sameGroundIds || []).length === 0}
                        className="ground-clear-btn"
                      >
                        Clear
                      </FormButton>
                    </div>

                    <div className="ground-selected-list">
                      {(sameGroundIds || []).length === 0 ? (
                        <small className="ground-empty-text">No grounds selected yet.</small>
                      ) : (
                        sameGroundIds.map((id) => {
                          const ground = (existingGrounds || []).find(
                            (item) => String(item._id || item.id) === String(id)
                          );

                          return (
                            <div key={id} className="ground-selected-item">
                              <div className="ground-selected-name">{ground?.name || id}</div>

                              <FormButton
                                type="button"
                                variant="secondary"
                                onClick={() =>
                                  setSameGroundIds((prev) =>
                                    prev.filter((item) => String(item) !== String(id))
                                  )
                                }
                                className="ground-remove-btn"
                              >
                                ×
                              </FormButton>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </FormField>
                </div>
              )}

              {sharedSpaceType === "SPLIT" && (
                <div className="ground-shared-block">
                  <div className="ground-grid ground-nested-grid">
                    <FormField label="Choose layout">
                      <FormSelect
                        value={splitLayoutMode}
                        onChange={(event) => {
                          setSplitLayoutMode(event.target.value);
                          clearError("splitLayoutId");
                          clearError("splitLayoutName");
                          clearError("splitMaxParts");
                        }}
                      >
                        <option value="existing">Use existing layout</option>
                        <option value="new">Create new layout</option>
                      </FormSelect>
                    </FormField>

                    {splitLayoutMode === "existing" ? (
                      <FormField label="Select existing layout" error={errors.splitLayoutId}>
                        <FormSelect
                          value={splitLayoutId}
                          onChange={(event) => {
                            setSplitLayoutId(event.target.value);
                            clearError("splitLayoutId");
                          }}
                          disabled={loadingLayouts}
                        >
                          {loadingLayouts && <option value="">Loading layouts…</option>}

                          {!loadingLayouts && layouts.length === 0 && (
                            <option value="">No layouts found (create new)</option>
                          )}

                          {!loadingLayouts &&
                            layouts.map((layout) => (
                              <option key={layout._id} value={layout._id}>
                                {layout.name} (max {layout.maxParts})
                              </option>
                            ))}
                        </FormSelect>
                      </FormField>
                    ) : (
                      <>
                        <FormField label="Layout name" error={errors.splitLayoutName}>
                          <FormInput
                            type="text"
                            value={splitLayoutName}
                            onChange={(event) => {
                              setSplitLayoutName(event.target.value);
                              clearError("splitLayoutName");
                            }}
                            placeholder="e.g. Basketball Half Court Layout"
                          />
                        </FormField>

                        <FormField label="Max parts" error={errors.splitMaxParts}>
                          <FormInput
                            type="number"
                            min="1"
                            value={splitMaxParts}
                            onChange={(event) => {
                              setSplitMaxParts(event.target.value);
                              clearError("splitMaxParts");
                            }}
                            placeholder="e.g. 2"
                          />
                        </FormField>
                      </>
                    )}

                    <FormField label="Space usage mode">
                      <FormSelect
                        value={splitUsageMode}
                        onChange={(event) => {
                          const nextMode = event.target.value;
                          setSplitUsageMode(nextMode);

                          if (nextMode === "custom") {
                            setSplitRole("CUSTOM");
                            setSplitSizeUsed((prev) => prev || "");
                          } else {
                            setSplitRole("FULL");
                            setSplitSizeUsed("2");
                          }

                          clearError("splitRole");
                          clearError("splitSizeUsed");
                        }}
                      >
                        <option value="preset">Preset split setup</option>
                        <option value="custom">Custom space usage</option>
                      </FormSelect>
                    </FormField>

                    {splitUsageMode === "preset" ? (
                      <FormField
                        label="Booking type"
                        error={errors.splitRole || errors.splitSizeUsed}
                        hint="Full blocks both halves. Half A & Half B can coexist, but each blocks Full."
                      >
                        <FormSelect
                          value={splitRole}
                          onChange={(event) => {
                            const role = event.target.value;
                            setSplitRole(role);
                            setSplitSizeUsed(role === "FULL" ? "2" : "1");
                            clearError("splitRole");
                            clearError("splitSizeUsed");
                          }}
                        >
                          <option value="FULL">Full ground (uses 2/2)</option>
                          <option value="HALF_A">Half A (uses 1/2)</option>
                          <option value="HALF_B">Half B (uses 1/2)</option>
                        </FormSelect>
                      </FormField>
                    ) : (
                      <FormField
                        label="Space consumed"
                        error={errors.splitSizeUsed}
                        hint="Use this for custom shared-space mappings like Volleyball = 2, Badminton = 2, Half Court = 1."
                      >
                        <FormInput
                          type="number"
                          min="1"
                          value={splitSizeUsed}
                          onChange={(event) => {
                            setSplitRole("CUSTOM");
                            setSplitSizeUsed(event.target.value);
                            clearError("splitSizeUsed");
                          }}
                          placeholder="e.g. 2"
                        />
                      </FormField>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <FormField label="Main Image" error={errors.mainImageUrl} className="ground-span-2">
            <div className="ground-upload-row">
              <FormInput
                type="file"
                accept="image/*"
                onChange={(event) => handleFilePick(event.target.files?.[0])}
                disabled={uploading || submitting}
              />

              <FormButton
                type="button"
                variant="primary"
                onClick={doUpload}
                disabled={!selectedFile || uploading || submitting}
                className="ground-upload-btn"
              >
                {uploading ? `Uploading ${progress || 0}%` : "Upload"}
              </FormButton>
            </div>

            {selectedFile && !uploading && (
              <small className="ground-selected-file">Selected: {selectedFile.name}</small>
            )}

            {previewSrc && (
              <div className="ground-image-preview">
                <img src={previewSrc} alt="Ground preview" />
              </div>
            )}
          </FormField>

          <FormField label="Base Price *" error={errors.basePrice}>
            <FormInput
              type="number"
              min="0"
              value={form.pricing.basePrice}
              onChange={(event) => {
                setPricing("basePrice", event.target.value);
                clearError("basePrice");
              }}
              placeholder="1000"
              required
            />
          </FormField>

          <FormField label="Currency">
            <FormInput type="text" value={venueCurrency} readOnly disabled />
          </FormField>

          <FormField label="Unit">
            <FormSelect
              value={form.pricing.unit}
              onChange={(event) => setPricing("unit", event.target.value)}
            >
              {UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </FormSelect>
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default VenueGroundForm;